import React from 'react';
import StationAvatar from './StationAvatar';

const SKELETON_COUNT = 8;

const StationsPage = ({
  stations,
  filteredStations,
  stationSearchTerm,
  selectedCountry,
  loading,
  loadedStationCount,
  currentPlayingStation,
  isPlaying,
  audioRef,
  getPopularityLevel,
  favoriteStations = [],
  onToggleFavorite,
  onStationSelect,
  onBack,
  onSearchChange,
  onSearchKeyPress,
  onClearSearch,
  pageTitle,
  pageSubtitle,
}) => {
  const displayedStations = stationSearchTerm ? filteredStations : stations;
  const isInitialLoad = loading && stations.length === 0;

  const isStationPlaying = (station) =>
    currentPlayingStation?.name === station.name &&
    isPlaying &&
    audioRef.current &&
    !audioRef.current.paused;

  const isFavorite = (station) =>
    favoriteStations.some((fav) =>
      (fav.id && station.id && fav.id === station.id) || fav.name === station.name
    );

  /* Build a readable detail line from available station metadata */
  const stationDetails = (station) => {
    const parts = [];

    // Genre / tags (first tag only, normalised)
    if (station.tags) {
      const tagList = station.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 1)
        .map((t) => t.split(' ')[0].slice(0, 14));
      if (tagList.length) parts.push(tagList[0]);
    }

    // Country
    const country = station.country_name || station.country;
    if (country) parts.push(country);

    // Bitrate
    if (station.bitrate && station.bitrate > 0) parts.push(`${station.bitrate} kbps`);

    return parts.join(' · ') || 'Radio station';
  };

  return (
    <div className="stations-page animate-fade-in">
      {/* Header */}
      <button className="back-btn" onClick={onBack} aria-label="Go back">
        ← Back
      </button>

      <div className="page-header">
        <h1 className="page-title">
          {pageTitle || (selectedCountry ? `${selectedCountry} Radio` : 'Stations')}
        </h1>
        <p className="page-subtitle">
          {pageSubtitle || 'Click any station to start listening'}
        </p>
      </div>

      {/* Search */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search stations…"
            value={stationSearchTerm}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyPress}
            aria-label="Search stations"
          />
          {stationSearchTerm && (
            <button
              className="search-clear-btn"
              onClick={onClearSearch}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isInitialLoad ? (
        <div className="skeleton-list">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      ) : displayedStations.length > 0 ? (
        <div className="stations-list">
          {displayedStations.map((station, index) => {
            const playing = isStationPlaying(station);
            return (
              <div
                key={`station-${station.id || index}`}
                className={`station-card${playing ? ' is-playing' : ''}`}
                onClick={() => onStationSelect(station)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStationSelect(station);
                  }
                }}
                aria-label={`${station.name}${playing ? ', now playing' : ''}`}
              >
                {/* Avatar: favicon → google favicon → initials → equalizer when playing */}
                <StationAvatar
                  name={station.name}
                  favicon={station.favicon}
                  homepage={station.homepage}
                  playing={playing}
                  size={48}
                />

                {/* Station details */}
                <div className="station-card-body">
                  <div className="station-card-name">{station.name}</div>
                  <div className="station-card-tags">{stationDetails(station)}</div>
                </div>

                {/* Right: popularity badge + play button */}
                <div className="station-card-right">
                  {onToggleFavorite && (
                    <button
                      className={`station-favorite-btn${isFavorite(station) ? ' is-active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(station);
                      }}
                      aria-label={
                        isFavorite(station)
                          ? `Remove ${station.name} from favourites`
                          : `Add ${station.name} to favourites`
                      }
                      tabIndex={-1}
                    >
                      {isFavorite(station) ? '♥' : '♡'}
                    </button>
                  )}
                  <span className="station-popularity-badge">
                    {getPopularityLevel(station)}
                  </span>
                  <button
                    className="station-play-btn"
                    onClick={(e) => { e.stopPropagation(); onStationSelect(station); }}
                    aria-label={`Play ${station.name}`}
                    tabIndex={-1}
                  >
                    {playing ? '⏸' : '▶'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : stationSearchTerm ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <div className="empty-state-title">No stations match "{stationSearchTerm}"</div>
          <div className="empty-state-desc">Try a different search term</div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">📻</div>
          <div className="empty-state-title">No stations found</div>
          <div className="empty-state-desc">
            No stations are available for {selectedCountry} at the moment.
          </div>
        </div>
      )}
    </div>
  );
};

export default StationsPage;
